/**
 * localStorage behind one guard. Reading the property itself throws with
 * cookies blocked outright and in some private modes, and a write throws
 * when the quota is full. Every call here swallows that, so a blocked store
 * reads as empty and a lost write only costs the next visit. The keys are
 * the union below, the one list of what the client stores, which the
 * privacy page describes in prose.
 */

/** Every key the client stores: the settings and the login as legacy kept them, the lobby's name draft, the
 *  changelog mark last opened, the radio messages under StarMash's key, and where the three dev panels were left. */
export type StorageKey =
  'settings' | 'auth' | 'playerName' | 'changelogSeen' | 'radioMessages' | 'devTestPanel' | 'debugPanel' | 'hitchPanel';

/** The string stored under key, or null when there is none or storage is blocked. */
export function loadString(key: StorageKey): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Stores a string, dropping it when storage is blocked or full. */
export function saveString(key: StorageKey, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Blocked storage or quota: the value still applies for this session
  }
}

/** Removes the key, where storage allows. */
export function removeKey(key: StorageKey): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Blocked storage: nothing stored to remove
  }
}

/** Wipes every key on the origin, for ?reset and the debug panel's clear button. */
export function clearStorage(): void {
  try {
    window.localStorage.clear();
  } catch {
    // Blocked storage: nothing stored to clear
  }
}

/** The JSON object stored under key, or null when there is none, it does not parse, it holds a primitive or an
 *  array, which JSON can also hold, or storage is blocked. */
export function loadJsonObject(key: StorageKey): Record<string, unknown> | null {
  const stored = loadString(key);
  if (stored === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Stores an object or an array as JSON. Nothing here reads a primitive back, and a stringified undefined would be
 *  stored as the text "undefined", so the type keeps both out. */
export function saveJson(key: StorageKey, value: object): void {
  saveString(key, JSON.stringify(value));
}

/** A parsed JSON object: not null, which typeof also calls an object, and not an array. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
